/**
 * Mini-onboarding du module Absences.
 * Affiche une carte d'activation en 3 étapes tant que la config n'est pas complète.
 */
import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import { Badge } from '../ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Sparkles, CheckCircle2, Loader2, Layers, Calendar, RefreshCcw, X,
} from 'lucide-react';
import api from '../../lib/api';
import { toast } from 'sonner';

const STEPS = [
  { key: 'typesConfigured', label: 'Activer les 14 types d\'absence par défaut', icon: Layers },
  { key: 'holidaysConfigured', label: 'Configurer les jours fériés annuels', icon: Calendar },
  { key: 'accrualLaunched', label: 'Lancer l\'acquisition initiale des soldes', icon: RefreshCcw },
];

export default function AbsencesOnboardingCard({ onComplete }) {
  const [status, setStatus] = useState(null);
  const [country, setCountry] = useState('ML');
  const [busy, setBusy] = useState(null);
  const [dismissed, setDismissed] = useState(() => localStorage.getItem('absencesOnboardingDismissed') === '1');

  const refresh = useCallback(async () => {
    try {
      const res = await api.get('/leave/onboarding-status');
      setStatus(res.data);
    } catch (e) {
      // silent
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  if (!status || dismissed) return null;
  if (status.isComplete) return null;

  const doSeedTypes = async () => {
    try {
      setBusy('types');
      const res = await api.post('/leave/types/seed-defaults');
      toast.success(`${res.data.added} type(s) ajouté(s)`);
      await refresh();
      onComplete && onComplete();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
    finally { setBusy(null); }
  };

  const doSeedHolidays = async () => {
    try {
      setBusy('holidays');
      const year = new Date().getFullYear();
      const res = await api.post('/leave/holidays/seed', { country, year });
      toast.success(`${res.data.added} jour(s) férié(s) ajouté(s) pour ${year}`);
      await refresh();
      onComplete && onComplete();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
    finally { setBusy(null); }
  };

  const doAccrual = async () => {
    try {
      setBusy('accrual');
      const res = await api.post('/leave/balances/run-accrual');
      toast.success(`Acquisition lancée : ${res.data.processed} employé(s) traités`);
      await refresh();
      onComplete && onComplete();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
    finally { setBusy(null); }
  };

  const handleDismiss = () => {
    localStorage.setItem('absencesOnboardingDismissed', '1');
    setDismissed(true);
  };

  return (
    <Card className="mb-6 border-indigo-200 bg-gradient-to-br from-indigo-50 to-white" data-testid="absences-onboarding-card">
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-3 gap-3 flex-wrap">
          <div className="flex items-center gap-2.5">
            <div className="p-2 rounded-lg bg-indigo-100">
              <Sparkles className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900">Activez votre module Absences en 2 minutes</h3>
              <p className="text-xs text-slate-600 mt-0.5">
                {status.stats.activeEmployees} employé(s) actif(s) — préparez votre socle RH en 3 étapes.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="bg-white">{status.progress}% complété</Badge>
            <Button size="sm" variant="ghost" onClick={handleDismiss} data-testid="onboarding-dismiss"
              title="Masquer cet assistant">
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <Progress value={status.progress} className="h-2 mb-4" />

        <div className="space-y-2">
          {STEPS.map((s, idx) => {
            const done = status.steps[s.key];
            const Icon = s.icon;
            const isBusy = busy === ['types', 'holidays', 'accrual'][idx];
            return (
              <div key={s.key} className="flex items-center justify-between p-3 rounded-lg bg-white border border-slate-100 gap-3 flex-wrap">
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${done ? 'bg-emerald-100' : 'bg-slate-100'}`}>
                    {done ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <Icon className="w-4 h-4 text-slate-500" />}
                  </div>
                  <div className="min-w-0">
                    <p className={`text-sm font-medium ${done ? 'text-slate-500 line-through' : 'text-slate-900'}`}>
                      <span className="text-xs text-slate-400 mr-1">Étape {idx + 1}</span>
                      {s.label}
                    </p>
                    {idx === 0 && (
                      <p className="text-xs text-slate-500">
                        {done ? `${status.stats.typesCount} type(s) configuré(s)` : 'Crée congé annuel, maladie, maternité, etc.'}
                      </p>
                    )}
                    {idx === 1 && (
                      <p className="text-xs text-slate-500">
                        {done ? `${status.stats.holidaysCount} jour(s) férié(s)` : 'Pré-remplir selon votre pays'}
                      </p>
                    )}
                    {idx === 2 && (
                      <p className="text-xs text-slate-500">
                        {done ? `${status.stats.accrualMovementsThisYear} mouvement(s) cette année` : 'Calcul automatique au pro-rata d\'ancienneté'}
                      </p>
                    )}
                  </div>
                </div>

                {!done && idx === 0 && (
                  <Button size="sm" onClick={doSeedTypes} disabled={!!busy} data-testid="onboarding-step-types">
                    {isBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Activer'}
                  </Button>
                )}

                {!done && idx === 1 && (
                  <div className="flex items-center gap-2">
                    <Select value={country} onValueChange={setCountry}>
                      <SelectTrigger className="w-[140px] h-8 text-xs" data-testid="onboarding-country-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ML">🇲🇱 Mali</SelectItem>
                        <SelectItem value="SN">🇸🇳 Sénégal</SelectItem>
                        <SelectItem value="CI">🇨🇮 Côte d'Ivoire</SelectItem>
                        <SelectItem value="FR">🇫🇷 France</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button size="sm" onClick={doSeedHolidays} disabled={!!busy} data-testid="onboarding-step-holidays">
                      {isBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Pré-remplir'}
                    </Button>
                  </div>
                )}

                {!done && idx === 2 && (
                  <Button size="sm" onClick={doAccrual} disabled={!!busy} data-testid="onboarding-step-accrual">
                    {isBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Lancer'}
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
